import {
  Button,
  DateField,
  Disclaimer,
  Form,
  Option,
  Select,
  TextAreaField,
  TextField,
} from '@repo/design-system-kit';
import { type FormEvent, type ReactNode, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type DateValue, parseDate } from '@internationalized/date';
import cx from 'clsx';
import type { Key } from 'react-aria';
import { useQuery } from '@tanstack/react-query';
import { useFlags } from '@repo/poly-hooks/flags';
import { FAQLink } from '@repo/shared-kit/components';
import {
  type ApiOrganization,
  registerOrganizationQueries,
} from '@repo/api-kit/queries/register-organization';
import type { ApiDocument } from '@repo/api-kit/queries/register-documents';
import { DocumentUploader } from '../../../common/components/document-uploader';
import { useDocuments } from '../../../documents';
import { EUROPEAN_STANDARD_REGISTER_LEGAL_FORMS } from '../../constants';
import { RISKY_NACE_CODES } from './risky-nace-codes';
import styles from './styles.module.css';

const MAX_DATE = parseDate(`${new Date().getFullYear() + 1}-01-01`);
const MIN_DATE = parseDate('1800-01-01');

interface NetherlandsFormProps {
  organization: ApiOrganization;
  isSubmitting: boolean;
  isNavigatingBack: boolean;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

export function NetherlandsForm({
  organization,
  isSubmitting,
  isNavigatingBack,
  onSubmit,
}: NetherlandsFormProps): ReactNode {
  const { formatMessage } = useIntl();
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // Fetch the org via React Query so documentIds reflects any previously uploaded documents.
  // staleTime: Infinity means one fetch per session (no redundant refetches).
  // On reload the fetch re-runs and returns the real document_ids from the API,
  // so uploaded documents persist across page reloads and back/forward navigation.
  // The upload/delete mutations' updateParentDocumentIds keep the cache current mid-session.
  const { data: cachedOrg } = useQuery({
    ...registerOrganizationQueries.item(organization.id),
    staleTime: Infinity,
  });

  const { documents } = useDocuments(cachedOrg?.documentIds ? [...cachedOrg.documentIds] : []);
  const kbisDocs = documents.filter((d: ApiDocument) => d.docType === 'kbis');
  const bylawDocs = documents.filter((d: ApiDocument) => d.docType === 'bylaw');

  const hasUploadedFile = kbisDocs.length > 0;
  const hasUploadedBylawsFile = bylawDocs.length > 0;

  const [legalRegistrationDate, setLegalRegistrationDate] = useState<DateValue | null>(
    organization.legalRegistrationDate ? parseDate(organization.legalRegistrationDate) : null
  );
  const [legalFormCreditsafeCode, setLegalFormCreditsafeCode] = useState(
    organization.legalFormCreditsafeCode || ''
  );
  const flags = useFlags();

  const NETHERLANDS_LEGAL_FORMS = EUROPEAN_STANDARD_REGISTER_LEGAL_FORMS.NL.filter(code => {
    if (code === 'NL206') {
      return Boolean(flags.featureBooleanNlEnableStichting);
    }
    return true;
  });

  const hasUploadFileIsInvalid = isFormSubmitting && !hasUploadedFile;
  const hasUploadBylawsFileIsInvalid = isFormSubmitting && !hasUploadedBylawsFile;

  const isRegisteredAndReferenced = !organization.underRegistration && !organization.unreferenced;
  const shouldCollectKbis = organization.unreferenced;

  const isNameDisabled = isRegisteredAndReferenced && Boolean(organization.name);
  const isLegalFormDisabled =
    isRegisteredAndReferenced && Boolean(organization.legalFormCreditsafeCode);

  const isLegalNumberDisabled = isRegisteredAndReferenced && Boolean(organization.legalNumber);
  const isLegalRegistrationDateDisabled =
    isRegisteredAndReferenced && Boolean(organization.legalRegistrationDate);

  const isStichtingEnabled =
    Boolean(flags.featureBooleanNlEnableStichting) && legalFormCreditsafeCode === 'NL206';

  const shouldShowActivityDescription =
    isStichtingEnabled ||
    (organization.legalSector && RISKY_NACE_CODES.includes(organization.legalSector)) ||
    !isRegisteredAndReferenced;

  const shouldShowWebsiteField =
    Boolean(flags.improvementActivityDescriptionNl) || isStichtingEnabled;

  const shouldCollectBylaws = isStichtingEnabled;

  const validateActivityDescription = (value: string): string | null => {
    if (!value) {
      return formatMessage({
        id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.error-message',
      });
    }
    if (value.length > 1000) {
      return formatMessage({
        id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.error-message-long-input',
      });
    }

    if (value.length < 60) {
      return formatMessage({
        id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.error-message-short-input',
      });
    }
    return null;
  };

  const validateLegalForm = (value: Key): string | null => {
    if (!isLegalFormDisabled && !value) {
      return formatMessage({ id: 'validations.errors.blank' });
    }
    return null;
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (shouldCollectKbis && !hasUploadedFile) {
      return;
    }

    if (shouldCollectBylaws && !hasUploadedBylawsFile) {
      return;
    }

    onSubmit(event);
  };

  // Upload/delete mutations call updateParentDocumentIds which updates the org cache,
  // causing kbisDocs/bylawDocs to update reactively via useDocuments. No manual
  // local state needed — hasUploadedFile is derived from kbisDocs.length above.

  return (
    <Form onSubmit={handleFormSubmit}>
      <h1 className="heading-section mb-32" data-test-register-organization-info-form-title>
        <FormattedMessage id="sign_up.add_business" />
      </h1>
      <h2 className="body-prominent mb-16">
        <FormattedMessage id="customer-registration-kit.sign_up.legal_information" />
      </h2>
      <p
        className={cx(styles.description, 'body-standard', 'mb-16')}
        data-test-register-organization-info-form-subtitle
      >
        <FormattedMessage id="customer-registration-kit.organizations.info.netherlands-form.legal-information.subtitle" />
      </p>
      {/* Organization Name */}
      <div className="mb-16">
        <TextField
          data-test-register-organization-info-form-name
          defaultValue={organization.name || ''}
          errorMessage={formatMessage({ id: 'validations.errors.blank' })}
          isReadOnly={Boolean(isNameDisabled)}
          isRequired={!isNameDisabled}
          label={formatMessage({ id: 'generic.legal-name.label' })}
          name="name"
          placeholder={formatMessage(
            { id: 'placeholders.organization_name' },
            { legalCountry: 'NL' }
          )}
          type="text"
        />
      </div>
      {/* Legal Form Select */}
      <div className="mb-16" data-test-register-organization-info-form-type>
        <Select
          data-test-register-organization-info-form-select
          defaultSelectedKey={legalFormCreditsafeCode}
          isReadOnly={isLegalFormDisabled}
          isRequired={!isLegalFormDisabled}
          label={formatMessage({ id: 'generic.legal-form.label' })}
          name="legalFormCreditsafeCode"
          onSelectionChange={key => {
            setLegalFormCreditsafeCode(String(key));
          }}
          placeholder={formatMessage(
            { id: 'placeholders.organization_type' },
            { legalCountry: 'NL' }
          )}
          validate={validateLegalForm}
        >
          {NETHERLANDS_LEGAL_FORMS.map(code => (
            <Option data-test-legal-form-code={code} id={code} key={code}>
              {/* oxlint-disable-next-line @repo/no-dynamic-intl-id */}
              {formatMessage({ id: `sign_up.company_type_option.NL.${code}` })}
            </Option>
          ))}
        </Select>
      </div>

      {/* Registration Number */}
      <div className="mb-16">
        <TextField
          data-test-register-organization-info-form-registration-number
          defaultValue={organization.legalNumber || ''}
          errorMessage={formatMessage({ id: 'validations.errors.blank' })}
          isReadOnly={isLegalNumberDisabled}
          isRequired={!isLegalNumberDisabled}
          label={formatMessage(
            { id: 'geo-expansion.organization-info.form.registration-number.label' },
            { legalCountry: 'NL' }
          )}
          name="legalNumber"
          placeholder={formatMessage(
            { id: 'geo-expansion.organization-info.form.registration-number.placeholder' },
            { legalCountry: 'NL' }
          )}
          type="text"
        />
      </div>
      {/* Legal Registration Date */}
      <div className={shouldCollectKbis && !shouldCollectBylaws ? 'mb-32' : 'mb-16'}>
        <DateField
          customValidationMessages={{
            valueMissing: formatMessage({ id: 'validations.errors.blank' }),
            rangeUnderflow: formatMessage({ id: 'validations.errors.date' }),
            rangeOverflow: formatMessage({ id: 'validations.errors.date' }),
          }}
          data-test-register-organization-info-form-registration-date
          isReadOnly={isLegalRegistrationDateDisabled}
          isRequired={!isLegalRegistrationDateDisabled}
          label={formatMessage({ id: 'generic.registration-date.label' })}
          maxValue={MAX_DATE}
          minValue={MIN_DATE}
          name="legalRegistrationDate"
          onChange={setLegalRegistrationDate}
          value={legalRegistrationDate}
        />
      </div>
      {/* Bylaws upload for Foundation (Stichting) */}
      {shouldCollectBylaws ? (
        <div className="mb-32" data-test-register-organization-info-bylaws-section>
          <DocumentUploader
            data-test-register-organization-info-form-bylaws
            docType="bylaw"
            documents={bylawDocs}
            errorMessage={
              hasUploadBylawsFileIsInvalid
                ? formatMessage({
                    id: 'customer-registration-kit.organizations.info.netherlands-form.bylaws.error-message',
                  })
                : undefined
            }
            label={formatMessage({
              id: 'customer-registration-kit.organizations.info.netherlands-form.bylaws.title',
            })}
            subjectId={organization.id}
            subjectType="Organization"
          />
        </div>
      ) : null}
      {/* Disclaimer block */}
      {!shouldCollectKbis && (
        <Disclaimer.Block className="mb-32" data-test-manually-add-info level="info">
          <FormattedMessage
            id="sign_up.verify_company_disclaimer"
            values={{ legalCountry: 'NL', htmlSafe: true }}
          />
        </Disclaimer.Block>
      )}
      {/* Activity Description */}
      {shouldShowActivityDescription ? (
        <div className="mb-32">
          <h2 className="body-prominent mb-16">
            <FormattedMessage id="customer-registration-kit.organizations.info.netherlands-form.activity-description.title" />
          </h2>
          <TextAreaField
            className={cx(shouldShowWebsiteField && 'mb-16')}
            data-test-register-organization-info-form-activity-description
            defaultValue={organization.activityDescription || ''}
            description={formatMessage({
              id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.hint-text',
            })}
            isExpandable
            isRequired
            label={formatMessage({
              id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.subtitle',
            })}
            name="activityDescription"
            placeholder={
              isRegisteredAndReferenced
                ? formatMessage(
                    {
                      id: 'organizations.profile.company_profile.organization_edit.activity_description_placeholder',
                    },
                    { organizationName: organization.legalName || organization.name }
                  )
                : formatMessage({
                    id: 'customer-registration-kit.organizations.info.netherlands-form.activity-description.placeholder',
                  })
            }
            showCharCount
            validate={validateActivityDescription}
          />
          {shouldShowWebsiteField ? (
            <TextField
              data-test-register-organization-info-form-website
              defaultValue={organization.websiteUrl || ''}
              inputMode="url"
              label={formatMessage({
                id: 'customer-registration-kit.organizations.info.netherlands-form.activity-website.label',
              })}
              name="websiteUrl"
              placeholder={formatMessage({
                id: 'customer-registration-kit.organizations.info.netherlands-form.activity-website.placeholder',
              })}
              type="text"
            />
          ) : null}
        </div>
      ) : null}
      {/* Document upload for unreferenced companies */}
      {shouldCollectKbis ? (
        <>
          <h2 className="body-prominent mb-16">
            <FormattedMessage id="customer-registration-kit.organizations.info.netherlands-form.unreferenced-certificate-title" />
          </h2>
          <p
            className={cx(styles.description, 'body-standard', 'mb-16')}
            data-test-register-organization-certificate-info
          >
            <FormattedMessage
              id="customer-registration-kit.organizations.info.netherlands-form.proof-of-registration.subtitle"
              values={{
                i: chunks => <i>{chunks}</i>,
                link: chunks => (
                  <FAQLink articleSlug="company_registered_required_documents">{chunks}</FAQLink>
                ),
              }}
            />
          </p>
          <DocumentUploader
            className="mb-16"
            data-test-register-organization-info-form-kbis
            docType="kbis"
            documents={kbisDocs}
            errorMessage={
              hasUploadFileIsInvalid ? formatMessage({ id: 'validations.errors.blank' }) : undefined
            }
            label={formatMessage({
              id: 'customer-registration-kit.organizations.info.netherlands-form.kbis.label',
            })}
            subjectId={organization.id}
            subjectType="Organization"
          />
        </>
      ) : null}
      <Button
        className={styles.submitButton}
        data-test-register-organization-info-form-submit
        isDisabled={isSubmitting || isNavigatingBack}
        isPending={isSubmitting}
        onClick={() => {
          setIsFormSubmitting(true);
        }}
        stretch
        type="submit"
      >
        <FormattedMessage id="generic.continue.cta" />
      </Button>
    </Form>
  );
}
